import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail } from "lucide-react";

const KEY = "dcl_newsletter_subscribed";

/**
 * A quiet, standalone newsletter signup — wires directly to Beehiiv when the
 * BEEHIIV_API_KEY + BEEHIIV_PUBLICATION_ID env vars are set on the backend.
 * Until then, signups queue locally and get retro-synced once the keys arrive.
 *
 * Props:
 *   variant="card"  → full missal card with heading & description (default)
 *   variant="strip" → compact single-row band suitable for embedding under other content
 */
const NewsletterForm = ({ variant = "card", title = "The Weekly Letter", description = "A quiet Sunday letter — reflections on the week's readings, a short prayer, and word of anything new. Nothing more.", ctaLabel = "Subscribe" }) => {
    const [firstName, setFirstName] = useState("");
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [subscribed, setSubscribed] = useState(() => !!localStorage.getItem(KEY));

    const submit = async (e) => {
        e.preventDefault();
        if (!firstName.trim() || !email.trim()) { toast.error("Name and email, please"); return; }
        setSubmitting(true);
        try {
            const r = await api.post("/newsletter/subscribe", { first_name: firstName.trim(), email: email.trim() });
            localStorage.setItem(KEY, email);
            setSubscribed(true);
            toast.success("Peace be with you", { description: r.data.message });
        } catch (err) {
            toast.error("Something went awry", { description: err?.response?.data?.detail || "Please try again." });
        } finally { setSubmitting(false); }
    };

    if (subscribed) {
        return (
            <div className={variant === "card" ? "missal-card p-8 text-center" : "border border-liturgy/40 px-6 py-4 text-center"} data-testid="newsletter-subscribed">
                <div className="small-caps text-[11px] accent-liturgy">You are with us</div>
                <p className={variant === "card" ? "mt-3 text-sm text-muted-foreground italic" : "mt-1 text-xs text-muted-foreground italic"}>
                    A Sunday letter will find you this week. Thank you for walking with us.
                </p>
            </div>
        );
    }

    if (variant === "strip") {
        return (
            <form onSubmit={submit} className="border border-border missal-card p-4 md:p-5 flex items-center gap-3 flex-wrap" data-testid="newsletter-strip">
                <Mail size={18} className="accent-liturgy hidden md:block" />
                <div className="flex-1 min-w-0">
                    <div className="small-caps text-[11px] accent-liturgy">{title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">Sunday reflection · free · unsubscribe anytime</div>
                </div>
                <input value={firstName} onChange={e => setFirstName(e.target.value)} data-testid="nl-first"
                    placeholder="First name"
                    className="bg-transparent border-b border-border px-2 py-1.5 text-sm w-32 focus:outline-none focus:border-liturgy" />
                <input value={email} onChange={e => setEmail(e.target.value)} type="email" data-testid="nl-email"
                    placeholder="Email"
                    className="bg-transparent border-b border-border px-2 py-1.5 text-sm flex-1 min-w-[180px] focus:outline-none focus:border-liturgy" />
                <button data-testid="nl-submit" disabled={submitting}
                    className="px-4 py-2 bg-liturgy text-white text-xs small-caps disabled:opacity-40 whitespace-nowrap">
                    {submitting ? "Sending…" : ctaLabel}
                </button>
            </form>
        );
    }

    return (
        <form onSubmit={submit} className="missal-card p-8 md:p-10 relative overflow-hidden" data-testid="newsletter-card">
            <div className="absolute top-0 right-0 w-40 h-40 -mr-16 -mt-16 rounded-full opacity-10 bg-liturgy" />
            <div className="relative">
                <div className="small-caps text-[11px] accent-liturgy mb-2 flex items-center gap-2">
                    <Mail size={12} /> {title}
                </div>
                <h3 className="font-serif text-2xl mb-3">Walk with us, one letter a week.</h3>
                <p className="text-sm text-muted-foreground italic leading-relaxed mb-6 max-w-lg">{description}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input value={firstName} onChange={e => setFirstName(e.target.value)} data-testid="nl-first"
                        placeholder="First name"
                        className="bg-transparent border border-border px-4 py-3 text-sm focus:outline-none focus:border-liturgy" />
                    <input value={email} onChange={e => setEmail(e.target.value)} type="email" data-testid="nl-email"
                        placeholder="Email address"
                        className="bg-transparent border border-border px-4 py-3 text-sm focus:outline-none focus:border-liturgy" />
                </div>
                <button data-testid="nl-submit" disabled={submitting}
                    className="mt-4 w-full py-3 bg-liturgy text-white small-caps disabled:opacity-40">
                    {submitting ? "Sending…" : ctaLabel}
                </button>
                <p className="mt-3 text-xs text-muted-foreground">Delivered by Beehiiv · unsubscribe with a single tap.</p>
            </div>
        </form>
    );
};

export default NewsletterForm;
